import React, { useEffect, useState } from "react";
import MainLayout from "layouts/Main";
import { Typography, Box } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { grey } from "@mui/material/colors";
import Select from "components/Select";
import Table from "components/Table";
import { getTabsByRoleClientReports } from "helpers/tabs";
import { useAppDispatch, useAppSelector } from "hooks";
import { selectProductionParks } from "store/selectors";
import { fetchAllProductionPark } from "store/appSlice";
import { getSupplyPlanForecast } from "api/resources/supplyPlan";
import dayjs, { Dayjs } from "dayjs";

interface ForecastData {
  supplyPlanDate: string;
  productionDate: string;
  productionPark: {
    id: number;
    name: string;
    code: string;
  };
  data: {
    DayAhead: Array<{
      quarterHour: number;
      mtuStart: string;
      mtuEnd: string;
      capacity: number | null;
    }>;
    SIDC_IntradayAuction1: Array<{
      quarterHour: number;
      mtuStart: string;
      mtuEnd: string;
      capacity: number | null;
    }>;
    SIDC_IntradayAuction2: Array<{
      quarterHour: number;
      mtuStart: string;
      mtuEnd: string;
      capacity: number | null;
    }>;
    SIDC_IntradayAuction3: Array<{
      quarterHour: number;
      mtuStart: string;
      mtuEnd: string;
      capacity: number | null;
    }>;
  };
}

const Forecast = () => {
  const dispatch = useAppDispatch();
  const productionParks = useAppSelector(selectProductionParks);

  const [dateFilter, setDateFilter] = useState<Dayjs | null>(dayjs());
  const [productionParkOptions, setProductionParkOptions] = useState([]);
  const [selectedProductionPark, setSelectedProductionPark] = useState<
    number | ""
  >("");
  const [forecastData, setForecastData] = useState<ForecastData | null>(null);
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState<any[]>([]);

  useEffect(() => {
    dispatch(fetchAllProductionPark(0, 1000, {}));
  }, [dispatch]);

  useEffect(() => {
    const options: any[] = [];

    if (productionParks) {
      productionParks.content.map((park) => {
        options.push({ label: park.name, value: park.id });
      });

      setProductionParkOptions(options);

      if (productionParks.content.length > 0) {
        setSelectedProductionPark(productionParks.content[0].id);
      }
    }
  }, [productionParks]);

  useEffect(() => {
    if (selectedProductionPark && dateFilter) {
      setLoading(true);
      getSupplyPlanForecast(
        Number(selectedProductionPark),
        dayjs(dateFilter).format("YYYY-MM-DD"),
      )
        .then((response) => {
          setForecastData(response.data);
          setLoading(false);
        })
        .catch((error) => {
          console.error("Failed to fetch forecast data:", error);
          setForecastData(null);
          setLoading(false);
        });
    }
  }, [selectedProductionPark, dateFilter]);

  useEffect(() => {
    if (forecastData) {
      const rows = Array.from({ length: 96 }, (_, i) => i + 1).map(
        (quarter) => {
          const dayAheadData = forecastData.data.DayAhead?.find(
            (d) => d.quarterHour === quarter,
          );
          const ida1Data = forecastData.data.SIDC_IntradayAuction1?.find(
            (d) => d.quarterHour === quarter,
          );
          const ida2Data = forecastData.data.SIDC_IntradayAuction2?.find(
            (d) => d.quarterHour === quarter,
          );
          const ida3Data = forecastData.data.SIDC_IntradayAuction3?.find(
            (d) => d.quarterHour === quarter,
          );

          return {
            quarter: "QH " + quarter,
            dayAhead: dayAheadData?.capacity
              ? Math.round(dayAheadData?.capacity / 4)
              : "-",
            ida1: ida1Data?.capacity ? Math.round(ida1Data?.capacity / 4) : "-",
            ida2: ida2Data?.capacity ? Math.round(ida2Data?.capacity / 4) : "-",
            ida3: ida3Data?.capacity ? Math.round(ida3Data?.capacity / 4) : "-",
          };
        },
      );
      setTableData(rows);
    } else {
      setTableData([]);
    }
  }, [forecastData]);

  const handleDateChange = (newValue: Dayjs | null) => {
    setDateFilter(newValue);
  };

  const handleProductionParkChange = (e: any) => {
    setSelectedProductionPark(e.target.value);
  };

  const columns = [
    {
      accessorKey: "quarter",
      header: "Quarter",
    },
    {
      accessorKey: "dayAhead",
      header: "DayAhead",
      meta: { isNumeric: true },
    },
    {
      accessorKey: "ida1",
      header: "IDA-1",
      meta: { isNumeric: true },
    },
    {
      accessorKey: "ida2",
      header: "IDA-2",
      meta: { isNumeric: true },
    },
    {
      accessorKey: "ida3",
      header: "IDA-3",
      meta: { isNumeric: true },
    },
  ];

  return (
    <MainLayout tabs={getTabsByRoleClientReports()}>
      <Typography variant="h5">Forecast report</Typography>
      <Box
        sx={{
          mt: 4,
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
        }}
      >
        <DatePicker
          label="Select date"
          sx={{ background: "white", mr: 2 }}
          slotProps={{
            textField: { size: "small" },
            clearButton: { size: "small" },
          }}
          value={dateFilter}
          onChange={handleDateChange}
        />
        <Select
          label="Production park"
          id="production-park"
          name="productionPark"
          minWidth={240}
          maxWidth={240}
          value={selectedProductionPark}
          onChange={handleProductionParkChange}
          options={productionParkOptions}
        />
      </Box>

      {forecastData && (
        <Box
          sx={{
            mt: 4,
            mb: 2,
            p: 2,
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            gap: "4rem",
            background: grey[200],
            maxWidth: "max-content",
          }}
        >
          <Typography>
            <strong>Forecast date:</strong>{" "}
            {dayjs(forecastData.supplyPlanDate).format("DD.MM.YYYY")}
          </Typography>
          <Typography>
            <strong>Production date:</strong>{" "}
            {dayjs(forecastData.productionDate).format("DD.MM.YYYY")}
          </Typography>
          <Typography>
            <strong>Time zone:</strong> Europe/Tallinn
          </Typography>
          <Typography>
            <strong>Unit:</strong> kWh
          </Typography>
        </Box>
      )}

      {selectedProductionPark && dateFilter && (
        <Box sx={{ maxWidth: "1200px" }}>
          <Table
            isFetching={loading}
            data={tableData}
            columns={columns}
            pageCount={1}
            noDataText="No forecast data available for selected date and production park"
          />
        </Box>
      )}
    </MainLayout>
  );
};

export default Forecast;
